import { captureMessage } from '@sentry/nextjs';

export type SubmitResult = { ok: true } | { ok: false; reason: 'network' | 'http-5xx' | 'http-4xx'; status?: number };

export type SubmitInput = {
  email: string;
  marketing_optin: boolean;
  newsletter_signup: boolean;
  journey_url: string;
  itinerary_id: string | null;
  locale: string;
};

const PROXY_URL = '/api/email-capture';
const TIMEOUT_MS = 4000;

function buildFailureContext(input: SubmitInput, result: Exclude<SubmitResult, { ok: true }>) {
  return {
    reason: result.reason,
    status: result.status,
    journey_url: input.journey_url,
    itinerary_id: input.itinerary_id,
    marketing_optin: input.marketing_optin
  };
}

function reportFailure(input: SubmitInput, result: Exclude<SubmitResult, { ok: true }>): SubmitResult {
  const extra = buildFailureContext(input, result);
  captureMessage('email_capture_submit_failed', { level: 'warning', extra });
  // biome-ignore lint/suspicious/noConsole: structured failure log surfaced to Vercel
  console.error('[email-capture] submit failed', extra);
  return result;
}

// proxied through /api/email-capture so the webhook URL stays server-side; cookie/navigation behaviour is unchanged
export async function submitEmailCapture(input: SubmitInput): Promise<SubmitResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      signal: controller.signal
    });

    if (response.ok) {
      return { ok: true };
    }

    const reason = response.status >= 500 ? 'http-5xx' : 'http-4xx';
    return reportFailure(input, { ok: false, reason, status: response.status });
  } catch {
    return reportFailure(input, { ok: false, reason: 'network' });
  } finally {
    clearTimeout(timeout);
  }
}
